import { useState } from 'react'

import { Dialog, OneDataCollection } from '@factorialco/f0-react/dist/experimental'
import { Add, Delete } from '@factorialco/f0-react/icons/app'
import {
  PlanningPeriodsDataCollectionDocument,
  PlanningValueObjectsPeriodTypeEnum,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useGraphqlDataCollectionSource } from 'modules/core/hooks/useGraphqlDataCollectionSource'
import buildUrl from 'modules/core/lib/buildUrl'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'
import {
  getPeriodStatusLabel,
  PLANNING_PERIOD_STATUS,
  PLANNING_PERIOD_STATUS_COLORS,
} from 'modules/planning/consts/period_status'
import usePeriods from 'modules/planning/hooks/usePeriods'
import { formatPeriod, getRequestDeadline } from 'modules/planning/lib/periodHelpers'
import { getCapabilities } from 'modules/planning/roles/capabilities'

import { PlanningPeriodNode } from 'modules/planning/types/queries'

const MAX_VISIBLE_AVATARS = 4

const getStatusFilterOptions = () =>
  Object.entries(PLANNING_PERIOD_STATUS).map(([, value]) => ({
    label: getPeriodStatusLabel(value),
    value,
  }))

const renderAvatarList = (
  employees: Array<{
    firstName: string
    lastName: string
    avatar?: { url: string } | null
  }>
) => {
  if (employees.length === 0) {
    return {
      type: 'text' as const,
      value: { placeholder: '-' },
    }
  }

  return {
    type: 'avatarList' as const,
    value: {
      type: 'person' as const,
      avatarList: employees.map((employee) => ({
        type: 'person',
        lastName: employee.lastName,
        firstName: employee.firstName,
        src: buildUrl(employee.avatar?.url ?? null),
      })),
      max: MAX_VISIBLE_AVATARS,
    },
  }
}

const toDateString = (value: unknown): string | undefined => {
  if (value instanceof Date) return value.toISOString().split('T')[0]
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}(T|$)/.test(value)) return value.split('T')[0]
  return undefined
}

interface PeriodsListProps {
  periodType: PlanningValueObjectsPeriodTypeEnum
  itemUrl: (periodId: string) => string
  handleCreatePeriod?: () => void
  setCanCreatePeriod?: (canCreate: boolean) => void
}

const PeriodsList = ({
  periodType,
  itemUrl,
  handleCreatePeriod,
  setCanCreatePeriod,
}: PeriodsListProps) => {
  const { company } = useCurrentSession()
  const { remove } = usePeriods({ companyId: company.id })
  const [periodToDelete, setPeriodToDelete] = useState<string | null>(null)
  const [isDeleting, setIsDeleting] = useState(false)

  const handleDelete = async () => {
    if (!periodToDelete) return

    setIsDeleting(true)
    const result = await remove(periodToDelete)
    setIsDeleting(false)

    if (result.success) {
      setPeriodToDelete(null)
      app.setFlash({
        type: 'correct',
        message: i18n.t('planning.periods_page.delete_dialog.success'),
      })
    }
  }

  const source = useGraphqlDataCollectionSource(
    {
      document: PlanningPeriodsDataCollectionDocument,
      mapData: (data) => {
        const canCreate = !!data.planning._policies?.periodsCanCreate?.can
        setCanCreatePeriod?.(canCreate)

        const edges = data.planning.periodsConnection?.edges ?? []

        return edges.map((edge) => edge.node).filter(Boolean)
      },
      mapVariables: ({ filters }) => {
        const periodRange = filters?.period
        const startDateGteq =
          periodRange && typeof periodRange === 'object' && 'from' in periodRange
            ? toDateString(periodRange.from)
            : undefined
        const startDateLteq =
          periodRange && typeof periodRange === 'object' && 'to' in periodRange
            ? toDateString(periodRange.to)
            : undefined

        return {
          companyId: company.id,
          periodTypes: [periodType],
          statuses: filters?.status && filters.status.length > 0 ? filters.status : undefined,
          startDateGteq,
          startDateLteq,
        }
      },
      mapPagination: (data) => ({
        total: data.planning.periodsConnection?.totalCount ?? 0,
      }),
      paginationType: 'pages',
      search: {
        enabled: true,
      },
      itemUrl: (item: PlanningPeriodNode) => itemUrl(item.id),
      primaryActions: () => {
        if (handleCreatePeriod) {
          return {
            label: i18n.t('planning.periods_page.actions.create'),
            icon: Add,
            onClick: handleCreatePeriod,
          }
        }

        return undefined
      },
      itemActions: (item: PlanningPeriodNode) => {
        const roles = item.periodRole?.[0]?.roles ?? []
        const capabilities = getCapabilities(roles)

        if (!capabilities.has('deletePeriod')) return []

        return [
          {
            critical: true,
            label: i18n.t('common.delete'),
            icon: Delete,
            onClick: () => setPeriodToDelete(item.id),
          },
        ]
      },
      filters: {
        status: {
          label: i18n.t('planning.periods_page.columns.status'),
          type: 'in',
          options: {
            options: getStatusFilterOptions(),
          },
        },
        period: {
          label: i18n.t('planning.periods_page.columns.period'),
          type: 'date',
          options: {
            mode: 'range',
          },
        },
      },
      presets: [
        {
          label: getPeriodStatusLabel(PLANNING_PERIOD_STATUS.ACTIVE),
          filter: { status: [PLANNING_PERIOD_STATUS.ACTIVE] },
        },
        {
          label: getPeriodStatusLabel(PLANNING_PERIOD_STATUS.CLOSED),
          filter: { status: [PLANNING_PERIOD_STATUS.CLOSED] },
        },
      ],
    },
    [company.id, periodType, handleCreatePeriod, setCanCreatePeriod, itemUrl]
  )

  return (
    <>
      <OneDataCollection
        source={source}
        emptyStates={{
          'no-data': {
            title: i18n.t('planning.periods_page.empty_state.title'),
            description: i18n.t('planning.periods_page.empty_state.description'),
          },
        }}
        visualizations={[
          {
            type: 'table',
            options: {
              columns: [
                {
                  label: i18n.t('planning.periods_page.columns.name'),
                  render: (item: PlanningPeriodNode) => ({
                    type: 'text' as const,
                    value: item.name,
                  }),
                },
                {
                  label: i18n.t('planning.periods_page.columns.period'),
                  render: (item: PlanningPeriodNode) => ({
                    type: 'text' as const,
                    value: formatPeriod(item),
                  }),
                },
                {
                  label: i18n.t('planning.periods_page.columns.status'),
                  render: (item: PlanningPeriodNode) => ({
                    type: 'status' as const,
                    value: {
                      label: getPeriodStatusLabel(item.status),
                      status: PLANNING_PERIOD_STATUS_COLORS[item.status],
                    },
                  }),
                },
                {
                  label: i18n.t('planning.periods_page.columns.owner'),
                  render: (item: PlanningPeriodNode) => ({
                    type: 'person' as const,
                    value: {
                      firstName: item.owner?.firstName ?? '',
                      lastName: item.owner?.lastName ?? '',
                      src: buildUrl(item.owner?.avatar?.url ?? null),
                    },
                  }),
                },
                {
                  label: i18n.t('planning.periods_page.columns.planners'),
                  render: (item: PlanningPeriodNode) =>
                    renderAvatarList(item.planners.map((planner) => planner.employee)),
                },
                {
                  label: i18n.t('planning.periods_page.columns.viewers'),
                  render: (item: PlanningPeriodNode) =>
                    renderAvatarList(item.viewers.map((viewer) => viewer.employee)),
                },
                {
                  label: i18n.t('planning.periods_page.columns.request_deadline'),
                  render: (item: PlanningPeriodNode) => ({
                    type: 'text' as const,
                    value: getRequestDeadline(item) ?? '',
                  }),
                },
              ],
            },
          },
        ]}
      />
      {periodToDelete && (
        <Dialog
          open
          onClose={() => setPeriodToDelete(null)}
          header={{
            title: i18n.t('planning.periods_page.delete_dialog.title'),
            description: i18n.t('planning.periods_page.delete_dialog.description'),
            type: 'warning',
          }}
          actions={{
            primary: {
              icon: Delete,
              label: i18n.t('planning.periods_page.delete_dialog.confirm'),
              onClick: handleDelete,
              disabled: isDeleting,
            },
            secondary: {
              label: i18n.t('planning.periods_page.delete_dialog.cancel'),
              onClick: () => setPeriodToDelete(null),
              disabled: isDeleting,
            },
          }}
        />
      )}
    </>
  )
}

export default PeriodsList
